import { AddonBase } from './addon-base';

/**
 * Addon Cart Form Add Component
 * Handles adding addons to cart with proper state management and error handling
 */
export class AddonCartFormAdd extends AddonBase {
  static CONFIG = {
    SELECTORS: {
      SUBMIT_BUTTON: '[data-addon-form-submit]',
      PARENT_PROPERTIES_SCRIPT: 'script[data-addon-parent-line-item-properties]'
    },
    TEMPLATES: {
      ADDED_CTA: 'template[name="addon-added-cta"]'
    },
    CSS_CLASSES: {
      LOADING: 'addon-form--loading',
      SUCCESS: 'addon-form--success',
      ERROR: 'addon-form--error',
      ADDED: 'addon-form--added'
    },
    EVENTS: {
      ADD_START: 'addon-cart:add-start',
      ADD_SUCCESS: 'addon-cart:add-success',
      ADD_ERROR: 'addon-cart:add-error'
    },
    MESSAGES: {
      LOADING: 'ADDING...',
      SUCCESS: 'ADDED!',
      ERROR: 'ERROR',
      DEFAULT: 'ADD TO BOARD'
    },
    TIMEOUTS: {
      SUCCESS_FEEDBACK: 2000,
      ERROR_FEEDBACK: 3000
    }
  };

  constructor() {
    super();

    this._handleSubmit = this._handleSubmit.bind(this);
    this._handleButtonClick = this._handleButtonClick.bind(this);
  }

  static get observedAttributes() {
    return ['addon-id', 'parent-product-key', 'parent-product-id', 'disabled', 'default-state'];
  }

  /**
   * Get initial state for the component
   * @protected
   * @returns {Object} Initial state object
   */
  _getInitialState() {
    return {
      isLoading: false,
      isAdded: false,
      error: null
    };
  }

  /**
   * Get allowed state keys for default-state parsing
   * @protected
   * @returns {string[]} Array of allowed state keys
   */
  _getAllowedStateKeys() {
    return ['isAdded', 'isLoading', 'error'];
  }

  /**
   * Get state keys to preserve during reinitialization
   * @protected
   * @returns {string[]} Array of state keys to preserve
   */
  _getPreservedStateKeys() {
    return ['isLoading', 'error'];
  }

  /**
   * Validate required attributes
   * @protected
   */
  _validateAttributes() {
    const addonId = this.getAttribute('addon-id');
    const parentProductKey = this.getAttribute('parent-product-key');

    if (!addonId) {
      throw new Error('AddonCartFormAdd: addon-id attribute is required');
    }

    if (!parentProductKey) {
      throw new Error('AddonCartFormAdd: parent-product-key attribute is required');
    }

    this._addonId = addonId;
    this._parentProductKey = parentProductKey;
    this._parentProductId = this.getAttribute('parent-product-id');
  }

  /**
   * Cache frequently accessed DOM elements
   * @protected
   */
  _cacheDOMReferences() {
    const { SELECTORS, TEMPLATES } = AddonCartFormAdd.CONFIG;

    const submitButton = this.querySelector(SELECTORS.SUBMIT_BUTTON);
    if (!submitButton) {
      throw new Error('AddonCartFormAdd: Submit button not found');
    }

    this._domRefs.set('submitButton', submitButton);
    this._domRefs.set('form', this.tagName === 'FORM' ? this : this.querySelector('form'));

    // Store original button text
    this._originalButtonHtml = submitButton.innerHTML.trim() || AddonCartFormAdd.CONFIG.MESSAGES.DEFAULT;

    const addedCtaTemplate = this.querySelector(TEMPLATES.ADDED_CTA);
    if (addedCtaTemplate) {
      this._domRefs.set('addedCtaTemplate', addedCtaTemplate);
    }

  }

  /**
   * Setup event listeners with proper cleanup tracking
   * @protected
   */
  _setupEventListeners() {
    const submitButton = this._domRefs.get('submitButton');
    const form = this._domRefs.get('form');

    this._addEventListener(submitButton, 'click', this._handleButtonClick);

    if (form) {
      this._addEventListener(form, 'submit', this._handleSubmit);
    } else {
      this._addEventListener(this, 'submit', this._handleSubmit);
    }
  }

  /**
   * Setup accessibility attributes
   * @protected
   */
  _setupAccessibility() {
    const submitButton = this._domRefs.get('submitButton');

    if (!submitButton.hasAttribute('aria-describedby')) {
      submitButton.setAttribute('aria-describedby', `${this.id || 'addon-form'}-status`);
    }

    if (this._addonId) {
      submitButton.setAttribute('aria-describedby', `addon-${this._addonId}-description`);
    }

    submitButton.setAttribute('type', 'button');
  }

  /**
   * Handle form submission
   * @protected
   */
  async _handleSubmit(event) {
    event.preventDefault();
    await this._processAddition();
  }

  /**
   * Handle button click
   * @protected
   */
  async _handleButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this._state.isLoading || this.hasAttribute('disabled')) {
      return;
    }

    await this._processAddition();
  }

  /**
   * Handle attribute changes
   * @protected
   * @param {string} name - Attribute name
   * @param {string} newValue - New attribute value
   */
  _handleAttributeChange(name, newValue) {
    switch (name) {
      case 'disabled':
        this._updateDisabledState(newValue !== null);
        break;
      case 'addon-id':
        this._addonId = newValue;
        this._initializeState();
        this._render();
        break;
      case 'parent-product-key':
        this._parentProductKey = newValue;
        this._initializeState();
        this._render();
        break;
      case 'parent-product-id':
        this._parentProductId = newValue;
        break;
      case 'default-state':
        this._reinitializeState();
        break;
    }
  }

  /**
   * Process addon addition to cart
   * @private
   */
  async _processAddition() {
    if (this._state.isLoading) return;

    this._updateState({ isLoading: true, error: null });
    this._dispatchEvent(AddonCartFormAdd.CONFIG.EVENTS.ADD_START);

    try {
      const formData = this._getFormData();
      const cartOperations = await this._addonService.prepareAddOperations(formData);

      await this._executeCartOperations(cartOperations);

      this._updateState({
        isLoading: false,
        isAdded: true
      });

      this._dispatchEvent(AddonCartFormAdd.CONFIG.EVENTS.ADD_SUCCESS, formData);
      this._showSuccessFeedback();
      this._requestModalClose();

    } catch (error) {
      this._updateState({ isLoading: false });
      this._handleError('Failed to add addon to cart', error);
    }
  }

  /**
   * Get form data for addon addition
   * @private
   */
  _getFormData() {
    if (!this._addonId || !this._parentProductKey) {
      throw new Error('Missing required form data');
    }

    // Get parent properties from script element
    const parentProperties = this._getParentProperties();

    return {
      addonId: this._addonId,
      parentProductKey: this._parentProductKey,
      parentProductId: this._parentProductId,
      parentProperties,
      addonForm: this
    };
  }

  /**
   * Get parent properties from script element
   * @protected
   */
  _getParentProperties() {
    const { SELECTORS } = AddonCartFormAdd.CONFIG;
    const scriptElement = this.querySelector(SELECTORS.PARENT_PROPERTIES_SCRIPT);

    if (!scriptElement) {
      return {};
    }

    try {
      return JSON.parse(scriptElement.textContent);
    } catch (error) {
      console.warn('AddonCartFormAdd: Failed to parse parent properties', error);
      return {};
    }
  }

  /**
   * Render component based on current state
   * @private
   */
  _render() {
    this._updateButtonState();
    this._updateCSSClasses();
    this._updateAccessibility();
  }

  /**
   * Update button state and text
   * @private
   */
  _updateButtonState() {
    const button = this._domRefs.get('submitButton');
    if (!button) return;

    const { MESSAGES } = AddonCartFormAdd.CONFIG;

    if (this._state.isLoading) {
      button.textContent = MESSAGES.LOADING;
      button.disabled = true;
    } else if (this._state.isAdded) {
      if (this._domRefs.has('addedCtaTemplate')) {
        button.innerHTML = this._domRefs.get('addedCtaTemplate').innerHTML;
      } else {
        button.textContent = MESSAGES.SUCCESS;
      }

      button.disabled = true;
    } else {
      button.innerHTML = this._originalButtonHtml;
      button.disabled = this.hasAttribute('disabled');
    }
  }

  /**
   * Update CSS classes based on state
   * @private
   */
  _updateCSSClasses() {
    const { CSS_CLASSES } = AddonCartFormAdd.CONFIG;

    this.classList.toggle(CSS_CLASSES.LOADING, this._state.isLoading);
    this.classList.toggle(CSS_CLASSES.SUCCESS, this._state.isAdded);
    this.classList.toggle(CSS_CLASSES.ERROR, !!this._state.error);
    this.classList.toggle(CSS_CLASSES.ADDED, this._state.isAdded);
  }

  /**
   * Update accessibility attributes
   * @private
   */
  _updateAccessibility() {
    const button = this._domRefs.get('submitButton');
    if (!button) return;

    button.setAttribute('aria-busy', this._state.isLoading.toString());

    if (this._state.error) {
      const addonId = this._addonId || 'addon-form';
      button.setAttribute('aria-describedby', `${addonId}-error`);
    }
  }

  /**
   * Update disabled state
   * @private
   */
  _updateDisabledState(disabled) {
    const button = this._domRefs.get('submitButton');
    if (!button) return;

    if (disabled || this._state.isLoading || this._state.isAdded) {
      button.disabled = true;
    } else {
      button.disabled = false;
    }
  }

  /**
   * Show success feedback
   * @private
   */
  _showSuccessFeedback() {
    setTimeout(() => {
      if (this._state?.isAdded) {
        this._updateState({ isAdded: false });
      }
    }, AddonCartFormAdd.CONFIG.TIMEOUTS.SUCCESS_FEEDBACK);
  }

  /**
   * Request modal closure with success feedback delay
   * @private
   */
  _requestModalClose() {
    // Check if this component is inside a modal
    const modal = this.closest('.addon-modal');

    if (modal) {
      // Find the addon-details component that contains this modal
      const addonDetailsElement = modal.closest('addon-details');

      if (addonDetailsElement && addonDetailsElement.isOpen) {
        // Close the modal with a delay to show success feedback
        setTimeout(() => {
          addonDetailsElement.closeModal();
        }, 500);
      }
    }
  }

  /**
   * Get current state
   */
  get isLoading() {
    return this._state?.isLoading || false;
  }

  get isAdded() {
    return this._state?.isAdded || false;
  }

  /**
   * Programmatically trigger addon addition
   */
  async addAddon() {
    if (!this._state.isLoading) {
      return this._processAddition();
    }
  }

  /**
   * Reset form to initial state
   */
  reset() {
    this._updateState({
      isLoading: false,
      isAdded: false,
      error: null
    });
  }
}